/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/material';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import BreadcrumbLayout from 'components/layout/BreadcrumbLayout';
import { Wallet } from 'components/Wallet';
import { useTranslation } from 'react-i18next';
import { OrganisationResponse } from 'api';
import OrganisationAutocomplete from 'components/OrganisationPicker/OrganisationAutocomplete';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import {
  SectionTitle,
  SvgBuilding2Bold,
} from '@insights-ltd/design-library/components';
import TransferUnitsForm from './TransferUnitsForm';
import { useGetTransferUnits } from './useGetTransferUnits';

const TransferUnits = () => {
  const { t } = useTranslation();
  const {
    userId,
    isPending,
    isError,
    practitioner,
    practitionerId,
    wallet,
    organisation,
    organisations,
    hasMultiOrgs,
    isMyAccount,
  } = useGetTransferUnits();
  const currentOrg = organisation || organisations?.[0];
  const [selectedOrg, setOrganisation] = useState<
    OrganisationResponse | null | undefined
  >(currentOrg);

  useEffect(() => {
    if (typeof selectedOrg === 'undefined' && currentOrg) {
      setOrganisation(currentOrg);
    }
  }, [selectedOrg, setOrganisation, organisations, currentOrg]);

  if (isPending) {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (isError) {
    return <FullScreenError message={t('ui.event-management.generic.error')} />;
  }

  const breadCrumbs: { [key: string]: string } = isMyAccount
    ? {
        '/': t('ui.event-management.dashboard.home.title'),
        '/account': t('ui.event-management.my-account.title'),
      }
    : {
        '/': t('ui.event-management.dashboard.home.title'),
        '/organisations': t('ui.event-management.organisations.title'),
        [`/organisations/${organisation?.id}`]: organisation?.name || '',
        [`/organisations/${organisation?.id}/practitioners/${practitioner?.id}`]:
          practitioner?.fullName || '',
      };

  return (
    <BreadcrumbLayout
      titleKey="ui.event-management.title.transfer-units"
      activeTextKey="ui.event-management.transfer-units.heading"
      crumbs={breadCrumbs}
      headingKey="ui.event-management.transfer-units.heading"
      fullWidth
    >
      <Grid
        container
        columns={12}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Grid size={{ xs: 12, md: 8 }}>
          {hasMultiOrgs ? (
            <>
              <Grid
                sx={(theme) => ({
                  marginBottom: theme.spacing(spacingSizeMap.M),
                })}
              >
                <Box
                  sx={{
                    my: spacingSizeMap.L,
                  }}
                >
                  <SectionTitle
                    icon={{
                      icon: <SvgBuilding2Bold />,
                      iconColour: 'pink',
                    }}
                    text={t('ui.event-management.users.create.organisation', {
                      count: 1,
                    })}
                  />
                </Box>
                <OrganisationAutocomplete
                  variant="dropdown"
                  onSelect={(
                    org: React.SetStateAction<
                      OrganisationResponse | null | undefined
                    >,
                  ) => {
                    setOrganisation(org);
                  }}
                  currentOrg={selectedOrg ?? undefined}
                  disableClearable
                />
              </Grid>
              <br />
            </>
          ) : null}
          <TransferUnitsForm
            myWallet={wallet!}
            userId={userId!}
            organisationId={selectedOrg?.id ?? ''}
            initialPractitionerId={practitionerId}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Wallet
            availableUnits={wallet!.availableUnits}
            organisationId={selectedOrg?.id ?? ''}
            practitionerId={userId}
            isMyWallet
          />
        </Grid>
      </Grid>
    </BreadcrumbLayout>
  );
};

export default TransferUnits;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
